"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRight } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import { Suspense, useEffect } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { authService } from "@/api/auth";
import { orgService } from "@/api/org";
import { ApiError, handleApiError } from "@/api/utils";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";
import { trackSignUpConversion } from "@/lib/ad-conversions";
import * as analytics from "@/lib/analytics";
import { useAuthStore } from "@/lib/auth";

const formSchema = z.object({
  firstName: z.string().min(1, { message: "First name is required" }),
  lastName: z.string().min(1, { message: "Last name is required" }),
  companyName: z.string().min(1, { message: "Company name is required" }),
  phoneNumber: z.string().min(10, { message: "Phone number is required" }),
  email: z.string().email(),
  password: z.string().min(8, { message: "Password must be at least 8 characters long" }),
});

function SignUpFormStateInit() {
  const router = useRouter();
  const auth = useAuthStore();
  const searchParams = useSearchParams();
  useEffect(() => {
    if (searchParams?.get("token")) {
      localStorage.setItem("helios:plan_token", searchParams.get("token")!);
    } else if (searchParams?.get("trial")) {
      localStorage.setItem("helios:plan_trial", searchParams.get("trial")!);
    } else {
      localStorage.removeItem("helios:plan_token");
      localStorage.removeItem("helios:plan_trial");
    }
    if (auth.user) {
      router.replace(auth.gym ? "/messages" : "/onboarding/inbox");
    }
  }, []);
  return null;
}

export function SignUpForm() {
  const router = useRouter();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      companyName: "",
      phoneNumber: "",
      email: "",
      password: "",
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      await authService.signup({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phoneNumber: values.phoneNumber,
        password: values.password,
      });
      await orgService.create({ name: values.companyName });
      analytics.track("Account Created", { email: values.email });
      analytics.track("Organization Created", { name: values.companyName });
      trackSignUpConversion();
      router.push("/onboarding/inbox");
    } catch (err) {
      if (err instanceof ApiError) {
        handleApiError(err);
      }
    }
  }

  return (
    <>
      <Suspense>
        <SignUpFormStateInit />
      </Suspense>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <div className="space-y-3">
            <div className="grid grid-cols-2 space-x-4">
              <div className="col-span-1">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First name</FormLabel>
                      <FormControl>
                        <Input placeholder="First name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="col-span-1">
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last name</FormLabel>
                      <FormControl>
                        <Input placeholder="Last name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
            <FormField
              control={form.control}
              name="companyName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company name</FormLabel>
                  <FormControl>
                    <Input placeholder="Company name" autoComplete="organization" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phoneNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone number</FormLabel>
                  <FormControl>
                    <PhoneInput hideDropdown {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="Email" type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Create password</FormLabel>
                  <FormControl>
                    <Input placeholder="Password" type="password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <Button type="submit" className="group flex w-full" loading={form.formState.isSubmitting}>
            Get started
            <ArrowRight className="w-4 h-4 ml-2 relative transition-transform group-hover:translate-x-0.5" />
          </Button>
        </form>
      </Form>
      <div className="sm:mx-auto sm:w-full sm:max-w-md px-2 mt-4 text-center">
        <p className="text-muted-foreground text-[11px]">
          By submitting this form I agree to this site&#39;s{" "}
          <a href="/terms-of-use" target="_blank" rel="noreferrer" className="underline">
            terms of use
          </a>{" "}
          and{" "}
          <a href="/privacy-policy" target="_blank" rel="noreferrer" className="underline">
            privacy policy
          </a>
          .
        </p>
      </div>
    </>
  );
}
